import { ThemeProvider, type ThemeFontValues } from "@caspeco/casper-ui-library";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { setHTMLHeadAppProperties } from "components/app/app-helper";
import Spinner from "components/spinner/spinner";
import config from "config/app.config";
import { fallbackTheme } from "fallback-theme";
import { useEffect, useMemo } from "react";
import { ga, initialize } from "react-ga";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import routes from "routes";
import { UrlParamName } from "search-params";
import { asString } from "search-params/parse";
import "../../variables.less";
import BackendToggle from "components/backend-toggle/backend-toggle";

/**
 * Reads font family URL params (fontBody, fontHeader, fontSubheader) and returns
 * a ThemeFontValues object if any are present, or undefined to use library defaults.
 */
function useFontParams(): ThemeFontValues | undefined {
    return useMemo(() => {
        const body = asString(UrlParamName.FONT_BODY, null);
        const header = asString(UrlParamName.FONT_HEADER, null);
        const subheader = asString(UrlParamName.FONT_SUBHEADER, null);

        if (!body && !header && !subheader) return undefined;

        return {
            body: body || "sans-serif",
            header: header || body || "sans-serif",
            subheader: subheader || body || "sans-serif",
        };
    }, []);
}

const App = () => {
    const { data: settings } = useExternalBookingSettings();
    const fontParams = useFontParams();

    useEffect(() => {
        if (config.environment) {
            void setHTMLHeadAppProperties(config.environment);
        }
    }, []);

    useEffect(() => {
        if (!settings?.gaTrackingId) return;
        initialize(settings?.gaTrackingId);
        ga("create", settings?.gaTrackingId, "auto", {
            name: "customerTracking",
        });
        ga("customerTracking.send", "pageview", {
            page: window.location.pathname + window.location.search,
            anonymizeIp: true,
        });
    }, [settings?.gaTrackingId]);

    const themeColors = settings?.themeSettings?.colors || fallbackTheme;
    return (
        <ThemeProvider colors={themeColors} fonts={fontParams}>
            <RouterProvider router={createBrowserRouter(routes)} fallbackElement={<Spinner />} />
            {import.meta.env.VITE_ENABLE_BACKEND_TOGGLE === "true" && <BackendToggle />}
        </ThemeProvider>
    );
};

export default App;
